import type { WhatsAppInboxChat, WhatsAppInboxMessage } from '@zrsystem/contracts';
import {
  Avatar,
  Button,
  ButtonIcon,
  EmptyText,
  ShellIconChevronLeft,
  Spinner,
  listTablePrimaryInitials,
} from '@zrsystem/ui';
import { useEffect, useRef, useState, type ReactElement } from 'react';

import {
  WhatsAppInboxAttachConfirm,
  type WhatsAppInboxPendingAttachment,
} from '@/features/notification/components/whatsapp-inbox-attach-confirm.js';
import { WhatsAppInboxComposer } from '@/features/notification/components/whatsapp-inbox-composer.js';
import { WhatsAppInboxMessageBubble } from '@/features/notification/components/whatsapp-inbox-message-bubble.js';
import {
  WhatsAppInboxDeleteDialog,
  WhatsAppInboxEditDialog,
} from '@/features/notification/components/whatsapp-inbox-message-dialogs.js';
import { whatsAppInboxChatDisplayName } from '@/features/notification/lib/whatsapp-inbox-labels.js';
import { useWhatsAppInboxMessageOverlay } from '@/features/notification/lib/whatsapp-inbox-message-overlay.js';

export interface WhatsAppInboxThreadProps {
  readonly placeId: string;
  readonly chat: WhatsAppInboxChat;
  readonly messages: readonly WhatsAppInboxMessage[];
  readonly isLoading: boolean;
  readonly errorMessage: string | null;
  readonly isPhone: boolean;
  readonly isSending: boolean;
  readonly isUploading: boolean;
  readonly composerDraft: string;
  readonly composerError: string | null;
  readonly canLoadMore: boolean;
  readonly isLoadingMore: boolean;
  readonly onBack?: () => void;
  readonly onLoadMore: () => void;
  readonly onDraftChange: (value: string) => void;
  readonly onSubmitText: () => void;
  readonly onSubmitAudio: (file: File) => Promise<boolean>;
  readonly onComposerError: (message: string) => void;
  readonly onAttachFile: (file: File) => void;
  readonly pendingAttachments: readonly WhatsAppInboxPendingAttachment[];
  readonly selectedAttachmentIndex: number;
  readonly onSelectPendingAttachment: (index: number) => void;
  readonly onCancelPendingAttachment: () => void;
  readonly onConfirmPendingAttachment: (captions: readonly (string | undefined)[]) => void;
  readonly onAddPendingFiles: (files: readonly File[]) => void;
}

export function WhatsAppInboxThread({
  placeId,
  chat,
  messages,
  isLoading,
  errorMessage,
  isPhone,
  isSending,
  isUploading,
  composerDraft,
  composerError,
  canLoadMore,
  isLoadingMore,
  onBack,
  onLoadMore,
  onDraftChange,
  onSubmitText,
  onSubmitAudio,
  onComposerError,
  onAttachFile,
  pendingAttachments,
  selectedAttachmentIndex,
  onSelectPendingAttachment,
  onCancelPendingAttachment,
  onConfirmPendingAttachment,
  onAddPendingFiles,
}: WhatsAppInboxThreadProps): ReactElement {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const title = whatsAppInboxChatDisplayName(chat);
  const overlay = useWhatsAppInboxMessageOverlay(placeId);
  const visibleMessages = messages.filter((message) => !overlay.hidden.has(message.id));
  const [editing, setEditing] = useState<WhatsAppInboxMessage | null>(null);
  const [deleting, setDeleting] = useState<WhatsAppInboxMessage | null>(null);

  useEffect(() => {
    const anchor = messagesEndRef.current;
    if (anchor !== null) {
      anchor.scrollIntoView({ block: 'end' });
    }
  }, [messages, overlay.hidden]);

  return (
    <div
      className={
        isPhone ? 'whatsapp-inbox__thread whatsapp-inbox__thread--solo' : 'whatsapp-inbox__thread'
      }
    >
      <div className="whatsapp-inbox__thread-header">
        {isPhone && onBack !== undefined ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            className="zr-touch-target"
            aria-label="Voltar para chats"
            onClick={onBack}
          >
            <ButtonIcon>
              <ShellIconChevronLeft size={16} />
            </ButtonIcon>
          </Button>
        ) : null}
        {chat.picture !== null ? (
          <Avatar size="lg" src={chat.picture} alt="" aria-hidden />
        ) : (
          <Avatar size="lg" aria-hidden>
            {listTablePrimaryInitials(title)}
          </Avatar>
        )}
        <strong>{title}</strong>
      </div>

      <div className="whatsapp-inbox__messages">
        {canLoadMore ? (
          <div className="whatsapp-inbox__load-more">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isLoadingMore}
              onClick={onLoadMore}
            >
              {isLoadingMore ? 'Carregando…' : 'Carregar anteriores'}
            </Button>
          </div>
        ) : null}
        {isLoading ? <Spinner aria-label="Carregando mensagens" /> : null}
        {!isLoading && errorMessage !== null ? <EmptyText>{errorMessage}</EmptyText> : null}
        {!isLoading && errorMessage === null && visibleMessages.length === 0 ? (
          <EmptyText>Conversa sem mensagens ainda. Envie a primeira.</EmptyText>
        ) : null}
        {visibleMessages.map((message) => {
          const editedBody = overlay.edited.get(message.id);
          const shown = editedBody === undefined ? message : { ...message, body: editedBody };
          return (
            <WhatsAppInboxMessageBubble
              key={message.id}
              message={shown}
              chatId={chat.id}
              edited={editedBody !== undefined}
              revoked={overlay.revoked.has(message.id)}
              onEdit={setEditing}
              onDelete={setDeleting}
            />
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <WhatsAppInboxComposer
        key={chat.id}
        draft={composerDraft}
        disabled={isLoading}
        isSending={isSending}
        isUploading={isUploading}
        errorMessage={composerError}
        onError={onComposerError}
        onDraftChange={onDraftChange}
        onSubmitText={onSubmitText}
        onSubmitAudio={onSubmitAudio}
        onAttachFile={onAttachFile}
      />
      {editing !== null ? (
        <WhatsAppInboxEditDialog
          placeId={placeId}
          chatId={chat.id}
          message={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
      {deleting !== null ? (
        <WhatsAppInboxDeleteDialog
          placeId={placeId}
          chatId={chat.id}
          message={deleting}
          onClose={() => {
            setDeleting(null);
          }}
        />
      ) : null}
      {pendingAttachments.length > 0 ? (
        <WhatsAppInboxAttachConfirm
          items={pendingAttachments}
          selectedIndex={selectedAttachmentIndex}
          uploading={isUploading}
          onSelect={onSelectPendingAttachment}
          onCancel={onCancelPendingAttachment}
          onConfirm={onConfirmPendingAttachment}
          onAddFiles={onAddPendingFiles}
        />
      ) : null}
    </div>
  );
}
